import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import StatusBadge, { STATES } from "@/components/StatusBadge";
import { Plus, Search, Plane, Calendar, User2, Filter } from "lucide-react";
import { fmtDateTime } from "@/lib/api";

const STATUS_TABS = [
  { value: "", label: "Todas" },
  { value: "EM_OPERACAO", label: "Em Operação" },
  { value: "EM_ANDAMENTO", label: "Em Andamento" },
  { value: "AGUARDANDO_FATURAMENTO", label: "Aguardando Faturamento" },
  { value: "FATURADO", label: "Faturado" },
];

export default function WorkOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/work-orders", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { fetch(); }, [status, fetch]);

  const grouped = useMemo(() => {
    const out = { EM_OPERACAO: 0, EM_ANDAMENTO: 0, AGUARDANDO_FATURAMENTO: 0, FATURADO: 0 };
    for (const wo of items) out[wo.status] = (out[wo.status] || 0) + 1;
    return out;
  }, [items]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Operações</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Ordens de Serviço</h1>
          <p className="text-sm text-slate-600 mt-1">Pousos, intake e ciclo de vida da OS no hangar.</p>
        </div>
        <Button
          className="h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm"
          onClick={() => navigate("/os/nova")}
          data-testid="new-work-order-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nova OS
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {Object.entries(STATES).map(([key, s]) => (
          <Card key={key} className={`p-4 rounded-sm border ${s.border} ${s.bg}`}>
            <div className={`text-[10px] tracking-[0.18em] uppercase font-bold ${s.text}`}>{s.label}</div>
            <div className={`font-display font-black text-3xl tabular-nums mt-1 ${s.text}`} data-testid={`kpi-${key.toLowerCase()}`}>
              {grouped[key] || 0}
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); fetch(); }}
          className="flex-1 flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por código, prefixo ou cliente…"
              className="pl-9 h-11 rounded-sm"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" variant="outline" className="h-11 rounded-sm" data-testid="search-button">
            <Filter className="w-4 h-4 mr-2" /> Filtrar
          </Button>
        </form>
        <div className="flex overflow-x-auto ag-scroll gap-1 bg-white border border-slate-200 rounded-sm p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              onClick={() => setStatus(t.value)}
              data-testid={`status-tab-${t.value || "all"}`}
              className={`px-3 h-9 text-xs font-medium uppercase tracking-wider rounded-sm whitespace-nowrap ${
                status === t.value ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <Card className="rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto ag-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-3 px-4 font-semibold">Código</th>
                <th className="py-3 px-4 font-semibold">Prefixo</th>
                <th className="py-3 px-4 font-semibold">Cliente</th>
                <th className="py-3 px-4 font-semibold">Origem → Destino</th>
                <th className="py-3 px-4 font-semibold">Chegada</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400 text-sm">Carregando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm">
                  Nenhuma ordem de serviço encontrada. Clique em &quot;Nova OS&quot; para iniciar.
                </td></tr>
              )}
              {!loading && items.map((wo) => (
                <tr
                  key={wo.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/os/${wo.id}`)}
                  data-testid={`wo-row-${wo.code}`}
                >
                  <td className="py-3 px-4 font-mono text-xs font-semibold text-slate-900">{wo.code}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <Plane className="w-3.5 h-3.5 text-slate-500" />
                      <span className="font-mono uppercase text-slate-900">{wo.tail_number}</span>
                    </div>
                    {wo.aircraft_model && <div className="text-xs text-slate-500">{wo.aircraft_model}</div>}
                  </td>
                  <td className="py-3 px-4 text-slate-700">{wo.client_name || "—"}</td>
                  <td className="py-3 px-4 text-slate-700 text-xs">
                    <span className="font-mono">{wo.flight_origin || "—"}</span>
                    <span className="text-slate-400 mx-1">→</span>
                    <span className="font-mono">{wo.flight_destination || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-xs font-mono">{fmtDateTime(wo.arrival_datetime)}</td>
                  <td className="py-3 px-4"><StatusBadge status={wo.status} /></td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid={`open-wo-${wo.code}`}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
